import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Clock,
    LogIn,
    LogOut,
    Calendar,
    User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Asistencia = () => {
    const [registros, setRegistros] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entradaModalOpen, setEntradaModalOpen] = useState(false);
    const [salidaModalOpen, setSalidaModalOpen] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const [form, setForm] = useState({
        empleado_id: '',
        fecha: new Date().toISOString().split('T')[0],
        hora_entrada: new Date().toTimeString().slice(0, 5),
        observaciones: ''
    });
    const [horaSalida, setHoraSalida] = useState(new Date().toTimeString().slice(0, 5));

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [asistRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/asistencia`),
                axios.get(`${API_URL}/api/empleados`)
            ]);
            setRegistros(asistRes.data);
            setEmpleados(empRes.data.filter(e => e.estatus === 'activo'));
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleEntradaSubmit = async (e) => {
        e.preventDefault();
        if (!form.empleado_id) {
            toast.error('Seleccione un empleado');
            return;
        }

        try {
            await axios.post(`${API_URL}/api/asistencia`, form);
            toast.success('Entrada registrada');
            setEntradaModalOpen(false);
            setForm({
                empleado_id: '',
                fecha: new Date().toISOString().split('T')[0],
                hora_entrada: new Date().toTimeString().slice(0, 5),
                observaciones: ''
            });
            fetchData();
        } catch (error) {
            toast.error('Error al registrar entrada');
        }
    };

    const handleSalidaSubmit = async (e) => {
        e.preventDefault();
        if (!selectedRegistro) return;

        try {
            await axios.put(`${API_URL}/api/asistencia/${selectedRegistro.id}/salida?hora_salida=${horaSalida}`);
            toast.success('Salida registrada');
            setSalidaModalOpen(false);
            setSelectedRegistro(null);
            fetchData();
        } catch (error) {
            toast.error('Error al registrar salida');
        }
    };

    const openSalidaModal = (registro) => {
        setSelectedRegistro(registro);
        setHoraSalida(new Date().toTimeString().slice(0, 5));
        setSalidaModalOpen(true);
    };

    const getEmpleadoNombre = (id) => {
        const emp = empleados.find(e => e.id === id);
        return emp ? emp.nombre : '-';
    };

    const getEmpleadoFoto = (id) => {
        const emp = empleados.find(e => e.id === id);
        return emp?.foto_url || '';
    };

    const EmpAvatar = ({ nombre, empleadoId, size = 'sm' }) => {
        const foto = getEmpleadoFoto(empleadoId);
        const initials = nombre ? nombre.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() : '?';
        const s = size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-5 h-5 text-[8px]';
        if (foto) return <img src={`${API_URL}${foto}`} alt={nombre} className={`${s} rounded-full object-cover border border-zinc-700 shrink-0`} />;
        return <div className={`${s} rounded-full bg-amber-400/15 flex items-center justify-center font-bold text-amber-400 shrink-0`}>{initials}</div>;
    };

    const registrosHoy = registros.filter(r => r.fecha === new Date().toISOString().split('T')[0]);
    const sinSalida = registrosHoy.filter(r => !r.hora_salida);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="asistencia-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Control de Asistencia
                    </h1>
                    <p className="text-zinc-400 mt-1">Registro de entradas y salidas de empleados</p>
                </div>
                <Button
                    onClick={() => setEntradaModalOpen(true)}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                    data-testid="registrar-entrada-btn"
                >
                    <LogIn className="w-5 h-5 mr-2" />
                    Registrar Entrada
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{registrosHoy.length}</p>
                            <p className="text-sm text-zinc-400">Registros Hoy</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                            <LogIn className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{registrosHoy.length - sinSalida.length}</p>
                            <p className="text-sm text-zinc-400">Con Salida</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-yellow-400/10 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{sinSalida.length}</p>
                            <p className="text-sm text-zinc-400">Sin Salida</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pending exits */}
            {sinSalida.length > 0 && (
                <Card className="card border-l-4 border-l-yellow-400">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold text-zinc-100 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-yellow-400" />
                            Pendientes de Salida
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {sinSalida.map((r) => (
                                <Button
                                    key={r.id}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openSalidaModal(r)}
                                    className="flex items-center gap-2"
                                    data-testid={`salida-${r.id}`}
                                >
                                    <EmpAvatar nombre={r.empleado_nombre || getEmpleadoNombre(r.empleado_id)} empleadoId={r.empleado_id} size="sm" />
                                    {r.empleado_nombre || getEmpleadoNombre(r.empleado_id)}
                                    <LogOut className="w-4 h-4 text-red-400" />
                                </Button>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Table */}
            <Card className="card">
                <CardHeader>
                    <CardTitle className="text-lg font-semibold">Registros de Hoy</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Empleado</TableHead>
                                    <TableHead className="table-header">Fecha</TableHead>
                                    <TableHead className="table-header">Entrada</TableHead>
                                    <TableHead className="table-header">Salida</TableHead>
                                    <TableHead className="table-header">Horas</TableHead>
                                    <TableHead className="table-header">Observaciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {registros.slice(0, 50).map((r) => (
                                    <TableRow key={r.id} className="table-row">
                                        <TableCell className="table-cell font-medium">
                                            <div className="flex items-center gap-2">
                                                <EmpAvatar nombre={r.empleado_nombre || getEmpleadoNombre(r.empleado_id)} empleadoId={r.empleado_id} />
                                                {r.empleado_nombre || getEmpleadoNombre(r.empleado_id)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell">{r.fecha}</TableCell>
                                        <TableCell className="table-cell">
                                            <span className="text-emerald-400">{r.hora_entrada}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {r.hora_salida ? (
                                                <span className="text-red-400">{r.hora_salida}</span>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => openSalidaModal(r)}
                                                    className="h-7 text-xs"
                                                >
                                                    Registrar
                                                </Button>
                                            )}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {r.horas_trabajadas ? (
                                                <span className="badge-info">{r.horas_trabajadas} hrs</span>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell className="table-cell text-zinc-400">
                                            {r.observaciones || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {registros.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-zinc-400">
                                            No hay registros de asistencia
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Entrada Modal */}
            <Dialog open={entradaModalOpen} onOpenChange={setEntradaModalOpen}>
                <DialogContent className="sm:max-w-[400px]" data-testid="entrada-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <LogIn className="w-5 h-5 inline mr-2 text-emerald-400" />
                            Registrar Entrada
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEntradaSubmit} className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Empleado *</Label>
                            <Select value={form.empleado_id} onValueChange={(v) => setForm({ ...form, empleado_id: v })}>
                                <SelectTrigger data-testid="select-empleado">
                                    <SelectValue placeholder="Seleccionar empleado" />
                                </SelectTrigger>
                                <SelectContent>
                                    {empleados.map(e => (
                                        <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha</Label>
                                <Input
                                    type="date"
                                    value={form.fecha}
                                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Hora Entrada</Label>
                                <Input
                                    type="time"
                                    value={form.hora_entrada}
                                    onChange={(e) => setForm({ ...form, hora_entrada: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Input
                                value={form.observaciones}
                                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                                placeholder="Notas (opcional)"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEntradaModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-entrada-btn">
                                Registrar Entrada
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Salida Modal */}
            <Dialog open={salidaModalOpen} onOpenChange={setSalidaModalOpen}>
                <DialogContent className="sm:max-w-[350px]" data-testid="salida-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <LogOut className="w-5 h-5 inline mr-2 text-red-400" />
                            Registrar Salida
                        </DialogTitle>
                    </DialogHeader>
                    {selectedRegistro && (
                        <div className="bg-zinc-800 p-3 rounded-lg mb-4">
                            <p className="font-medium">{selectedRegistro.empleado_nombre}</p>
                            <p className="text-sm text-zinc-400">Entrada: {selectedRegistro.hora_entrada}</p>
                        </div>
                    )}
                    <form onSubmit={handleSalidaSubmit} className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Hora de Salida</Label>
                            <Input
                                type="time"
                                value={horaSalida}
                                onChange={(e) => setHoraSalida(e.target.value)}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setSalidaModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-[#FA383E] hover:bg-[#e5333a]" data-testid="save-salida-btn">
                                Registrar Salida
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
